import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import STRINGS from "../Constants/Strings";
import SidebarMenu from "./SidebarMenu";
import MemberDetails from "./MemberDetails/index";
import MembersApplication from "./MembersApplication";
import MembersBusiness from "./MembersBusiness";
import MembersApplicationSearch from "./MembersApplicationSearch";
import BranchModal from "./BranchModal";
import * as ROUTES from "../Constants/Routes";
import {
  HTTP_STATUS,
  S_ACCOUNT_ID,
  S_APPLICANT_ID,
  S_CURRENT_MEMBER_ID,
  S_CURRENT_MEMBER_NAME,
  S_EMAIL,
  S_MEMBER_INFO_WELCOME_SHOWN,
  S_MSISDN,
} from "../Communication/Constants";
import {
  getApplicationList,
  getCompleteApplicant,
} from "../Redux/Actions/ApplicationAction";
import { getAccount, getAccountData } from "../Redux/Actions/GetAccountAction";
import { getProductFromVault } from "../Components/MarketPlace/Action/MarketPlaceAction";
import { removeBranchIdentifiers } from "../Utils/CommonUtilities";
import { doGetApplicantList } from "Redux/Actions/GetApplicantListAction";
import { format, subDays } from "date-fns";
import { usePersonalSearch } from "./MembersScreen/hooks/usePersonalSearch";
import PersonalMemberSearch from "./MembersScreen/components/PersonalMemberSearch";
import PersonalMembersTable from "./MembersScreen/components/PersonalMembersTable";
import {
  PersonalTabBar,
  BranchTabBar,
} from "./MembersScreen/components/TabNavigation";
import { formatPhoneNumber } from "./MembersScreen/utils/formatters";

const updateUrlParams = (accountId) => {
  if (window.history && window.history.pushState) {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    if (accountId) {
      params.set("tab", "members");
      params.set("id", accountId);
    } else {
      params.delete("tab");
      params.delete("id");
    }
    const newUrl = `${currentUrl.pathname}?${params.toString()}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  }
};

export default function MembersScreen({
  membersTab,
  search,
  recentMembers,
  handleTabClick,
  handleInputChange,
  handleSearch,
  handleResetSearch,
  activeMenu,
  onMenuClick,
  history,
  proceedWithApplication,
  productList,
  updateMemberName,
  memberAutoLoad,
  switchBanking,
  provinceList,
  doSetInSessionJointApplicant,
  doAccountSearch,
  doApplicationSearch,
  doUpdateApplicant,
  doDeleteApplication,
  doUpdateProductToVault,
  doFileList,
  doUploadFile,
  doAddDocument,
  doDeleteDocument,
  doGetDocumentList,
  doGetDocument,
  doGetApplicationProperty,
  employmentAndIncomeReducer,
  occupationCodes,
  idTypes,
  finInfo,
  doAccountCreate,
  complianceReducer,
  doGetProductInformation,
  doGetApplicationNotes,
  doUpdateApplicationNotesReview,
  doConfirmApp,
  doGetProductFromVault,
  doGetVault,
}) {
  const [personalTab, setPersonalTab] = useState("members");
  const [applicationStatus, setApplicationStatus] = useState("all");
  const [account, setAccount] = useState({});
  const personalSearch = usePersonalSearch(membersTab, doAccountSearch);

  // Application search state
  const [applicationId, setApplicationId] = useState("");
  const [timeframe, setTimeframe] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [applicationSearchResults, setApplicationSearchResults] = useState([]);
  const [applicationSearchLoading, setApplicationSearchLoading] = useState(
    false
  );
  const [
    applicationSearchPagination,
    setApplicationSearchPagination,
  ] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });
  const [applicationSearchError, setApplicationSearchError] = useState("");
  const [hasApplicationSearched, setHasApplicationSearched] = useState(false);
  const [applicationSortConfig, setApplicationSortConfig] = useState({
    sortBy: "created",
    sortDirection: "DESC",
  });
  const lastApplicationSearchParamsRef = useRef(null);

  // Member details state
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberDetails, setShowMemberDetails] = useState(false);
  const [showPersonalDetails, setShowPersonalDetails] = useState(true);
  const [showContactDetails, setShowContactDetails] = useState(true);
  const [showAddressDetails, setShowAddressDetails] = useState(true);
  const [showCreditDetails, setShowCreditDetails] = useState(true);
  const [showKycDetails, setShowKycDetails] = useState(true);
  const [showSecondaryIdDetails, setShowSecondaryIdDetails] = useState(true);
  const [memberApplicationsTab, setMemberApplicationsTab] = useState(
    "applications"
  );
  const [portfolioSubTab, setPortfolioSubTab] = useState("accounts");
  const [memberDetailsTab, setMemberDetailsTab] = useState("personal");
  const [applicant, setApplicant] = useState({});
  const [applicationList, setApplicationList] = useState([]);
  const [productVault, setProductVault] = useState([]);
  const [targetApplicationId, setTargetApplicationId] = useState(null);
  const [showNoProfileModal, setShowNoProfileModal] = useState(false);

  const dispatch = useDispatch();

  // Check URL params based on previous activity
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    const accountId = params.get("id");

    // Only auto-load member if BOTH tab and accountId are present
    // This prevents reloading when navigating back from My Desk
    if (tab === "members" && accountId) {
      handleViewMember(accountId, null, null, { fromApplicationSearch: true });
    }
  }, []);

  // Update from/to dates when timeframe changes
  useEffect(() => {
    const today = format(new Date(), "MM-dd-yyyy");
    setToDate(today);
    setFromDate(format(subDays(new Date(), timeframe), "MM-dd-yyyy"));
  }, [timeframe]);

  const handleTimeframeChange = (event) => {
    setTimeframe(parseInt(event.target.value, 10));
  };

  const handleApplicationIdChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    setApplicationId(value);
  };

  // Application Search Logic
  const executeApplicationSearch = useCallback(
    (searchParams, pageOverride = null, pageSizeOverride = null) => {
      console.log(
        "executeApplicationSearch: ",
        searchParams,
        pageOverride,
        pageSizeOverride
      );
      if (membersTab !== "personal" || personalTab !== "applications") return;

      setApplicationSearchError("");
      setApplicationSearchLoading(true);
      setHasApplicationSearched(true);

      if (pageOverride === null) {
        setApplicationSearchResults([]);
        setApplicationSearchPagination((prev) => ({
          ...prev,
          currentPage: 0,
          totalElements: 0,
          totalPages: 0,
        }));
      }

      lastApplicationSearchParamsRef.current = searchParams;

      let statusFilter = "";
      if (applicationStatus === "inprogress") statusFilter = "in-progress";
      else if (applicationStatus === "inreview") statusFilter = "in-review";
      else if (applicationStatus === "completed") statusFilter = "completed";

      const currentPage =
        pageOverride !== null
          ? pageOverride
          : applicationSearchPagination.currentPage;
      const pageSize =
        pageSizeOverride !== null
          ? pageSizeOverride
          : applicationSearchPagination.pageSize;

      const searchCriteria = {
        applicationId: searchParams.applicationId || "",
        startDate: format(
          subDays(new Date(), searchParams.timeframe || timeframe),
          "yyyy-MM-dd"
        ),
        status: statusFilter,
        page: currentPage,
        size: pageSize,
        sortBy: applicationSortConfig.sortBy,
        sortDirection: applicationSortConfig.sortDirection,
      };

      doApplicationSearch(searchCriteria, (response) => {
        setApplicationSearchLoading(false);
        if (response.error) {
          console.error("Application search failed:", response.error);
          setApplicationSearchResults([]);
          setApplicationSearchPagination((prev) => ({
            ...prev,
            totalElements: 0,
            totalPages: 0,
            hasNext: false,
            hasPrevious: false,
          }));
        } else {
          const data = response.data || response;
          setApplicationSearchResults(data?.results || []);
          setApplicationSearchPagination({
            currentPage: data?.currentPage || 0,
            pageSize: data?.pageSize || 20,
            totalElements: data?.totalElements || 0,
            totalPages: data?.totalPages || 0,
            hasNext: data?.hasNext || false,
            hasPrevious: data?.hasPrevious || false,
          });
        }
      });
    },
    [
      membersTab,
      personalTab,
      applicationStatus,
      applicationSortConfig.sortBy,
      applicationSortConfig.sortDirection,
      doApplicationSearch,
    ]
  );

  // Create refs to store current values for manual search
  const currentSearchValuesRef = useRef({});

  // Update refs whenever values change (but don't trigger re-renders)
  useEffect(() => {
    currentSearchValuesRef.current = {
      applicationId,
      fromDate,
      timeframe,
      applicationStatus,
    };
  }, [applicationId, fromDate, timeframe, applicationStatus]);

  const handleApplicationSearch = useCallback(() => {
    const currentValues = currentSearchValuesRef.current;
    console.log("handleApplicationSearch: ", currentValues);
    executeApplicationSearch({
      applicationId: currentValues.applicationId,
      fromDate: currentValues.fromDate,
      timeframe: currentValues.timeframe,
      status: currentValues.applicationStatus,
    });
  }, [executeApplicationSearch]);

  const handleApplicationSearchWithStoredParams = useCallback(
    (pageOverride = null, pageSizeOverride = null) => {
      if (lastApplicationSearchParamsRef.current) {
        executeApplicationSearch(
          lastApplicationSearchParamsRef.current,
          pageOverride,
          pageSizeOverride
        );
      }
    },
    [executeApplicationSearch]
  );

  useEffect(() => {
    if (
      hasApplicationSearched &&
      membersTab === "personal" &&
      personalTab === "applications" &&
      lastApplicationSearchParamsRef.current
    ) {
      handleApplicationSearchWithStoredParams(
        applicationSearchPagination.currentPage,
        applicationSearchPagination.pageSize
      );
    }
  }, [
    applicationSearchPagination.currentPage,
    applicationSearchPagination.pageSize,
    applicationSortConfig.sortBy,
    applicationSortConfig.sortDirection,
    hasApplicationSearched,
    membersTab,
    personalTab,
    handleApplicationSearchWithStoredParams,
  ]);

  useEffect(() => {
    console.log(
      "useEffect: ",
      applicationStatus,
      hasApplicationSearched,
      membersTab,
      personalTab,
      lastApplicationSearchParamsRef.current
    );
    if (
      hasApplicationSearched &&
      membersTab === "personal" &&
      personalTab === "applications" &&
      lastApplicationSearchParamsRef.current
    ) {
      const updatedParams = {
        ...lastApplicationSearchParamsRef.current,
        status: applicationStatus,
      };
      executeApplicationSearch(updatedParams);
    }
  }, [
    applicationStatus,
    executeApplicationSearch,
    hasApplicationSearched,
    membersTab,
    personalTab,
  ]);

  const handleApplicationSort = (field) => {
    const newDirection =
      applicationSortConfig.sortBy === field &&
      applicationSortConfig.sortDirection === "ASC"
        ? "DESC"
        : "ASC";
    setApplicationSortConfig({
      sortBy: field,
      sortDirection: newDirection,
    });
    setApplicationSearchPagination((prev) => ({ ...prev, currentPage: 0 }));
  };

  const handleApplicationKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleApplicationSearch();
      }
    },
    [handleApplicationSearch]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        personalSearch.handlePersonalSearch(search);
      }
    },
    [personalSearch, search]
  );

  // Auto-load member
  useEffect(() => {
    if (memberAutoLoad) {
      handleViewMember(memberAutoLoad);
    }
  }, [memberAutoLoad]);

  const refreshMemberDetails = (selectedMember) => {
    if (!selectedMember) return;

    dispatch(
      getAccount((res) => {
        if (res.status === HTTP_STATUS.OK) {
          setAccount(res.data || {});
          if (res.data) {
            sessionStorage.setItem(S_EMAIL, res.data.email);
            const msisdn =
              res.data.msisdn ||
              (res.data.homePhone && res.data.homePhone.length === 10
                ? `1${res.data.homePhone}`
                : res.data.homePhone);
            if (msisdn) sessionStorage.setItem(S_MSISDN, msisdn);
          }

          dispatch(
            getAccountData(sessionStorage.getItem(S_ACCOUNT_ID), (res) => {
              if (res.status === HTTP_STATUS.OK) {
                setSelectedMember(
                  res.data
                    ? {
                        accountId: sessionStorage.getItem(S_ACCOUNT_ID),
                        ...res.data,
                        applicantId:
                          selectedMember.applicantId || res.data.applicantId,
                      }
                    : null
                );

                if (selectedMember.applicantId || res.data.applicantId) {
                  sessionStorage.setItem(
                    S_APPLICANT_ID,
                    selectedMember.applicantId || res.data.applicantId
                  );
                  dispatch(
                    getCompleteApplicant(
                      selectedMember.applicantId || res.data.applicantId,
                      (res) => {
                        setApplicant(res.data || {});
                        if (res.data?.member?.name) {
                          sessionStorage.setItem(
                            S_CURRENT_MEMBER_NAME,
                            res.data.member.name
                          );
                          if (typeof updateMemberName === "function") {
                            updateMemberName(res.data.member.name);
                          }
                        } else {
                          sessionStorage.removeItem(S_CURRENT_MEMBER_NAME);
                        }
                      }
                    )
                  );
                  dispatch(
                    getApplicationList((res) =>
                      setApplicationList(
                        Array.isArray(res.data) ? res.data : []
                      )
                    )
                  );
                } else {
                  setApplicant({});
                  setApplicationList([]);
                }

                dispatch(
                  getProductFromVault((res) =>
                    setProductVault(Array.isArray(res.data) ? res.data : [])
                  )
                );
                dispatch(
                  doGetApplicantList((res) =>
                    console.log("applicant: ", res.data)
                  )
                );
              }
            })
          );
        }
      })
    );
  };

  const handleViewMember = (
    accountId,
    applicationId = null,
    applicantId = null,
    searchData = null
  ) => {
    console.log(
      "handleViewMember: ",
      accountId,
      applicationId,
      applicantId,
      searchData
    );
    // Remove stale applicant session
    sessionStorage.removeItem(S_APPLICANT_ID);

    if (!accountId) {
      setShowNoProfileModal(true);
      return;
    }

    // Reset stale member-related data
    setAccount({});
    setApplicant({});
    setApplicationList([]);
    setProductVault([]);
    setSelectedMember(null);

    // Store current member identifiers in session
    sessionStorage.setItem(S_CURRENT_MEMBER_ID, accountId);
    sessionStorage.setItem(S_ACCOUNT_ID, accountId);
    updateUrlParams(accountId);
    sessionStorage.removeItem(S_MEMBER_INFO_WELCOME_SHOWN);
    doSetInSessionJointApplicant(null);

    // Determine which tab to show
    let defaultTab = "overview"; // default for member clicks

    if (searchData?.fromApplicationSearch || applicationId) {
      defaultTab = "applications";
    } else if (!searchData && !applicationId) {
      // Only check URL params if not coming from search or application click
      const params = new URLSearchParams(window.location.search);
      if (params.get("tab") === "members" && params.get("id") === accountId) {
        defaultTab = "applications";
      }
    }

    setMemberApplicationsTab(defaultTab);

    // Track the target application if provided
    setTargetApplicationId(applicationId || null);

    // Find the member from search results
    const member = personalSearch.searchResults.find(
      (r) => r.accountId === accountId || r.applicantId === applicantId
    );

    const currentSelectedMember = {
      ...member,
      accountId,
      applicantId,
      applicationId,
    };

    setSelectedMember(currentSelectedMember);

    // Refresh all member-related details
    refreshMemberDetails(currentSelectedMember);

    setShowMemberDetails(true);
  };

  const handleBreadcrumbBackToApplications = () => {
    setMemberApplicationsTab("applications");
    setTargetApplicationId(null);
  };

  const handleBackToMembers = () => {
    removeBranchIdentifiers(dispatch);
    sessionStorage.removeItem(S_ACCOUNT_ID);
    sessionStorage.removeItem(S_EMAIL);
    sessionStorage.removeItem(S_MSISDN);

    // Reset all member-related state to prevent showing stale data
    setAccount({});
    setApplicant({});
    setApplicationList([]);
    setProductVault([]);
    setSelectedMember(null);

    setShowMemberDetails(false);
    updateUrlParams(null);
    if (typeof updateMemberName === "function") updateMemberName(null);
    handleResetSearch();
    personalSearch.resetSearch();
    setApplicationId("");
    setTimeframe(1);
    setApplicationStatus("all");
    setApplicationSearchResults([]);
    setApplicationSearchPagination({
      currentPage: 0,
      pageSize: 20,
      totalElements: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    });
    setHasApplicationSearched(false);
    lastApplicationSearchParamsRef.current = null;
  };

  const renderRecentMembersCards = () => {
    return (
      <ul className="branch-widget--recent-members__list">
        {recentMembers.map((m, idx) => (
          <li key={idx} className="branch-widget--recent-members__list-item">
            <Card className="branch-widget--recent-members__card">
              <Card.Body className="branch-widget--recent-members__body">
                <div className="branch-widget--recent-members__row">
                  {/* Avatar */}
                  <img
                    className="branch-widget--recent-members__avatar"
                    src={
                      m.avatar ||
                      `https://randomuser.me/api/portraits/${
                        Math.random() > 0.5 ? "men" : "women"
                      }/${Math.floor(Math.random() * 99) + 1}.jpg`
                    }
                    alt={`${m.name}'s profile photo`}
                  />

                  {/* Main content */}
                  <div className="branch-widget--recent-members__content">
                    <div className="branch-widget--recent-members__header">
                      <h3 className="branch-widget--recent-members__title">
                        {m.name}
                      </h3>

                      <button
                        type="button"
                        className="branch-widget--recent-members__action"
                        aria-label={`Open profile for ${m.name}`}
                      >
                        <i
                          className="fas fa-external-link-alt"
                          aria-hidden="true"
                        ></i>
                      </button>
                    </div>

                    <div className="branch-widget--recent-members__meta">
                      {m.action}
                    </div>

                    <div className="branch-widget--recent-members__transaction">
                      {m.transaction}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="branch-wrapper">
      <div className="container pb-3 main-container">
        <div className="row main-container__body">
          {/* Sidebar */}
          <div className="col-xl-2 col-lg-3 col-md-3 col-12">
            <SidebarMenu activeMenu={activeMenu} onMenuClick={onMenuClick} />
          </div>

          {/* Main Content */}
          <div className="col-xl-10 col-lg-9 col-md-9 col-12">
            <div className="row">
              {showMemberDetails ? (
                <>
                  {/* Member Details Panel */}
                  <div
                    className="col-xl-3 col-lg-3 col-md-3 col-12 branch-member-details"
                    style={{
                      borderRight: "1px solid #eee",
                      borderLeft: "1px solid #eee",
                      background: "#fff",
                      borderRadius: "0.5rem",
                      minHeight: "100vh",
                    }}
                  >
                    <MemberDetails
                      selectedMember={selectedMember}
                      account={account}
                      showPersonalDetails={showPersonalDetails}
                      setShowPersonalDetails={setShowPersonalDetails}
                      showContactDetails={showContactDetails}
                      setShowContactDetails={setShowContactDetails}
                      showAddressDetails={showAddressDetails}
                      setShowAddressDetails={setShowAddressDetails}
                      showCreditDetails={showCreditDetails}
                      setShowCreditDetails={setShowCreditDetails}
                      showKycDetails={showKycDetails}
                      setShowKycDetails={setShowKycDetails}
                      showSecondaryIdDetails={showSecondaryIdDetails}
                      setShowSecondaryIdDetails={setShowSecondaryIdDetails}
                      memberDetailsTab={memberDetailsTab}
                      setMemberDetailsTab={setMemberDetailsTab}
                      handleBackToMembers={handleBackToMembers}
                      formatPhoneNumber={formatPhoneNumber}
                      applicant={applicant}
                      doUpdateApplicant={doUpdateApplicant}
                      refreshMemberDetails={refreshMemberDetails}
                      provinceList={provinceList}
                      employmentAndIncomeReducer={employmentAndIncomeReducer}
                      occupationCodes={occupationCodes}
                      idTypes={idTypes}
                      finInfo={finInfo}
                      complianceReducer={complianceReducer}
                      doFileList={doFileList}
                      doUploadFile={doUploadFile}
                      history={history}
                    />
                  </div>

                  {/* Applications Panel */}
                  <div className="col-xl-9 col-lg-9 col-md-9 col-12">
                    <MembersApplication
                      memberApplicationsTab={memberApplicationsTab}
                      setMemberApplicationsTab={setMemberApplicationsTab}
                      selectedMember={selectedMember}
                      portfolioSubTab={portfolioSubTab}
                      setPortfolioSubTab={setPortfolioSubTab}
                      doFileList={doFileList}
                      doUploadFile={doUploadFile}
                      applicationList={applicationList}
                      productVault={productVault}
                      productList={productList}
                      proceedWithApplication={proceedWithApplication}
                      switchBanking={switchBanking}
                      history={history}
                      targetApplicationId={targetApplicationId}
                      doDeleteApplication={doDeleteApplication}
                      doUpdateProductToVault={doUpdateProductToVault}
                      refreshMemberDetails={refreshMemberDetails}
                      doAddDocument={doAddDocument}
                      doDeleteDocument={doDeleteDocument}
                      doGetDocumentList={doGetDocumentList}
                      doGetDocument={doGetDocument}
                      doGetApplicationProperty={doGetApplicationProperty}
                      applicant={applicant}
                      employmentAndIncomeReducer={employmentAndIncomeReducer}
                      complianceReducer={complianceReducer}
                      doGetProductInformation={doGetProductInformation}
                      finInfo={finInfo}
                      doGetApplicationNotes={doGetApplicationNotes}
                      doUpdateApplicationNotesReview={
                        doUpdateApplicationNotesReview
                      }
                      doConfirmApp={doConfirmApp}
                      doGetProductFromVault={doGetProductFromVault}
                      doGetVault={doGetVault}
                      handleBreadcrumbBackToApplications={
                        handleBreadcrumbBackToApplications
                      }
                      updateUrlParams={updateUrlParams}
                      setShowMemberDetails={setShowMemberDetails}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* TABS + SEARCH FORMS WIDGET */}
                  <div className="col-xl-9 col-lg-9 col-md-9 col-12">
                    <section
                      className="branch-widget branch-widget--search"
                      aria-labelledby="search-widget-title"
                    >
                      <Card className="branch-widget__card">
                        <Card.Body>
                          <div className="branch-widget__header">
                            <div className="branch-widget__header-left">
                              <i
                                className="fas fa-users branch-widget__icon"
                                aria-hidden="true"
                              ></i>
                              <h2
                                id="search-widget-title"
                                className="branch-widget__title"
                              >
                                {STRINGS.BRANCH.MEMBER_HUB.PAGE_TITLE}
                              </h2>
                            </div>
                          </div>

                          {/* Primary Tabs */}
                          <div className="branch-widget__tabs branch-widget__tabs--primary">
                            <BranchTabBar
                              membersTab={membersTab}
                              onPersonalClick={() => {
                                setApplicationId("");
                                personalSearch.resetSearch();
                                setHasApplicationSearched(false);
                                setTimeframe(1);
                                setApplicationStatus("all");
                                setApplicationSearchResults([]);
                                handleTabClick("personal");
                              }}
                              onBusinessClick={() => handleTabClick("business")}
                            />
                          </div>

                          {/* Secondary Tabs & Forms */}
                          <div className="branch-widget__tabs branch-widget__tabs--secondary">
                            {membersTab === "personal" ? (
                              <>
                                <PersonalTabBar
                                  personalTab={personalTab}
                                  onMembersClick={() => {
                                    handleResetSearch();
                                    personalSearch.resetSearch();
                                    setPersonalTab("members");
                                  }}
                                  onApplicationsClick={() => {
                                    setApplicationId("");
                                    setHasApplicationSearched(false);
                                    setTimeframe(1);
                                    setApplicationStatus("all");
                                    setApplicationSearchResults([]);
                                    setPersonalTab("applications");
                                  }}
                                />

                                {personalTab === "applications" ? (
                                  <MembersApplicationSearch
                                    // Form controls
                                    applicationId={applicationId}
                                    timeframe={timeframe}
                                    fromDate={fromDate}
                                    toDate={toDate}
                                    applicationStatus={applicationStatus}
                                    handleApplicationIdChange={
                                      handleApplicationIdChange
                                    }
                                    handleApplicationKeyDown={
                                      handleApplicationKeyDown
                                    }
                                    handleTimeframeChange={
                                      handleTimeframeChange
                                    }
                                    handleApplicationSearch={
                                      handleApplicationSearch
                                    }
                                    setApplicationStatus={setApplicationStatus}
                                    applicationSearchLoading={
                                      applicationSearchLoading
                                    }
                                    hasApplicationSearched={
                                      hasApplicationSearched
                                    }
                                    setHasApplicationSearched={
                                      setHasApplicationSearched
                                    }
                                    // Table-related props are NOT passed here → table will be hidden
                                    showTableOnly={false} // ← This ensures only form shows
                                  />
                                ) : (
                                  <PersonalMemberSearch
                                    search={search}
                                    handleInputChange={handleInputChange}
                                    handleSearch={() =>
                                      personalSearch.handlePersonalSearch(
                                        search
                                      )
                                    }
                                    handleReset={() => {
                                      handleResetSearch();
                                      personalSearch.resetSearch();
                                    }}
                                    handleKeyDown={handleKeyDown}
                                    provinceList={provinceList}
                                    searchError={personalSearch.searchError}
                                    emailError={personalSearch.emailError}
                                    setEmailError={personalSearch.setEmailError}
                                    mobileError={personalSearch.mobileError}
                                    setMobileError={
                                      personalSearch.setMobileError
                                    }
                                    homeError={personalSearch.homeError}
                                    setHomeError={personalSearch.setHomeError}
                                    sin4Error={personalSearch.sin4Error}
                                    setSin4Error={personalSearch.setSin4Error}
                                  />
                                )}
                              </>
                            ) : (
                              <MembersBusiness
                                search={search}
                                handleInputChange={handleInputChange}
                                handleSearch={handleSearch}
                                handleTabClick={handleTabClick}
                                provinceList={provinceList}
                              />
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </section>

                    {/* SEARCH RESULTS WIDGET */}
                    <section
                      className="branch-widget branch-widget--results mt-3"
                      aria-labelledby="results-widget-title"
                    >
                      <Card className="branch-widget__card">
                        <Card.Body>
                          <div className="branch-widget__header">
                            <div className="branch-widget__header-left">
                              <i
                                className="fas fa-search branch-widget__icon"
                                aria-hidden="true"
                              ></i>
                              <h2
                                id="results-widget-title"
                                className="branch-widget__title"
                              >
                                {STRINGS.BRANCH.MEMBER_HUB.RESULTS_TITLE}
                              </h2>
                            </div>
                          </div>
                          <div className="branch-widget__content">
                            {/* Personal Members Table */}
                            {membersTab === "personal" &&
                              personalTab === "members" && (
                                <PersonalMembersTable
                                  searchResults={personalSearch.searchResults}
                                  isLoading={personalSearch.isLoading}
                                  hasSearched={personalSearch.hasSearched}
                                  pagination={personalSearch.pagination}
                                  setPagination={personalSearch.setPagination}
                                  sortConfig={personalSearch.sortConfig}
                                  handleSort={personalSearch.handleSort}
                                  handleViewMember={handleViewMember}
                                  history={history}
                                  onRefresh={
                                    personalSearch.handlePersonalSearchWithStoredParams
                                  }
                                />
                              )}

                            {/* Personal Applications Table */}
                            {membersTab === "personal" &&
                              personalTab === "applications" && (
                                <MembersApplicationSearch
                                  applicationSearchResults={
                                    applicationSearchResults
                                  }
                                  applicationSearchLoading={
                                    applicationSearchLoading
                                  }
                                  applicationSearchPagination={
                                    applicationSearchPagination
                                  }
                                  hasApplicationSearched={
                                    hasApplicationSearched
                                  }
                                  applicationSortConfig={applicationSortConfig}
                                  applicationStatus={applicationStatus}
                                  setApplicationStatus={setApplicationStatus}
                                  setApplicationSearchPagination={
                                    setApplicationSearchPagination
                                  }
                                  handleApplicationSort={handleApplicationSort}
                                  handleViewMember={handleViewMember}
                                  setHasApplicationSearched={
                                    setHasApplicationSearched
                                  }
                                  timeframe={timeframe}
                                  showTableOnly={true}
                                />
                              )}
                          </div>
                        </Card.Body>
                      </Card>
                    </section>
                  </div>

                  {/* RECENT MEMBERS WIDGET */}
                  <div className="col-xl-3 col-lg-3 col-md-3 col-12">
                    <section
                      className="branch-widget branch-widget--recent-members"
                      aria-labelledby="recent-members-section-title"
                    >
                      <Card className="branch-widget__card">
                        <Card.Body>
                          <div className="branch-widget__header">
                            <div className="branch-widget__header-left">
                              <i
                                className="fas fa-users branch-widget__icon"
                                aria-hidden="true"
                              ></i>
                              <h2
                                id="recent-members-section-title"
                                className="branch-widget__title"
                              >
                                {STRINGS.BRANCH.MEMBER_HUB.RECENT_MEMBERS_TITLE}
                              </h2>
                            </div>
                          </div>
                          <div className="branch-widget__content">
                            {renderRecentMembersCards()}
                          </div>
                        </Card.Body>
                      </Card>
                    </section>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* No Profile Modal */}
      <BranchModal
        show={showNoProfileModal}
        onHide={() => setShowNoProfileModal(false)}
        title=""
        description="There's no marketplace profile associated with this member"
        buttonText="OK"
        onButtonClick={() => setShowNoProfileModal(false)}
      />
    </div>
  );
}
